import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useAppDispatch, useAppSelector } from "../../state/store";
import { CircularProgress, Grid } from "@mui/material";
import { Row } from "../../components/Row";
import { BQATabs } from "../../components/BQATabs/BQATabs";
import { EnumDescription } from "../../types/generalTypes";
import { SimpleDateInput, SimpleSelection } from "../../components/Inputs";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { DataTable } from "../../components/DataTable/DataTable";
import dayjs from "dayjs";
import { getShortenedBook } from "../../util/generalUtil";
import { Line } from "react-chartjs-2";
import { getChartLabelOptions } from "../../util/Chart";
import { Modal } from "../../components/Modal/Modal";
import { MaterialSelection } from "../../types/material";
import { getDateRangeDisplay, getTimeStampDisplay } from "../../util/dateUtils";
import { ReviewChapter, ReviewRun } from "../../types/reviewTypes";
import { constructReviewRun, verseCardsStateKey } from "./VerseCardsData";
import { useRequestHandler } from "../../hooks/requestHandler";
import { getTimeDisplay } from "../../util/timeUtil";
import {
  addReviewRun,
  deleteReviewRun,
  getReviewRuns,
} from "../../state/actions/review";

export function VerseCardsStats() {
  useDocumentTitle("Review Stats - Bible Quiz Academy");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const handler = useRequestHandler({
    onSuccess: (key) => {
      if (key === "SAVE") localStorage.removeItem(verseCardsStateKey);
      setRunModalOpen(false);
    },
  });
  const { runs, loading } = useAppSelector((state) => state.review);
  const material = useAppSelector((state) => state.material.material);
  const { lightMode } = useAppSelector((state) => state.settings);
  const { user } = useAppSelector((state) => state.authentication);
  const [readStarted, setReadStarted] = useState(false);
  useEffect(() => {
    if (user && !loading) {
      setReadStarted(true);
      dispatch(getReviewRuns());
    }
  }, []);

  // Filter Fields
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [chapterFilter, setChapterFilter] = useState<{
    book?: number;
    chapter?: number;
  }>({});
  useEffect(() => {
    // Default book, if you have only one
    const setDefaultBook = () => {
      let bookIndex = undefined;
      for (const run of runs) {
        for (const chapter of run.results) {
          if (bookIndex === undefined) {
            bookIndex = chapter.book;
          } else if (bookIndex !== chapter.book) {
            setChapterFilter({});
            return;
          }
        }
      }
      setChapterFilter({ book: bookIndex });
    };
    setDefaultBook();

    // Save previous verse cards, if it exists
    if (readStarted) {
      const previousVC = localStorage.getItem(verseCardsStateKey);
      if (previousVC) {
        const verseCards = JSON.parse(previousVC);
        if (verseCards.versesRemaining.length === 0) {
          const newRun = constructReviewRun(
            material,
            verseCards.verseStats,
            verseCards.runId,
            verseCards.time
          );
          handler.runRequest(addReviewRun(newRun), "", "", "SAVE");
        }
      }
    }
  }, [runs]);

  const [chartViewing, setChartViewing] = useState<
    "PERCENT" | "TIME" | "TOTAL"
  >("PERCENT");

  // Modal visibility and selection
  const [totalModalOpen, setTotalModalOpen] = useState(false);
  const [verseModalOpen, setVerseModalOpen] = useState(false);
  const [verseModalSelection, setVerseModalSelection] = useState({
    book: 0,
    chapter: 0,
    verse: 0,
  });

  const [chapterModalOpen, setChapterModalOpen] = useState(false);
  const [chapterViewing, setChapterViewing] = useState({
    book: 0,
    chapter: 0,
  });

  const [runModalOpen, setRunModalOpen] = useState(false);
  const [runIndexViewing, setRunIndexViewing] = useState(0);
  const [deletePhase, setDeletePhase] = useState(0); // 0: none. 1: details. 2: confirm

  const [percentHelpOpen, setPercentHelpOpen] = useState(false);
  const [chartHelpOpen, setChartHelpOpen] = useState(false);

  // Runs, filtered by chapter
  const chapterFilteredRuns = useMemo(() => {
    if (chapterFilter.book === undefined) return runs;
    const result: ReviewRun[] = [];
    runs.forEach((run) => {
      // Filter results by the chapter
      const chapters = run.results.filter(
        (chapter) =>
          chapter.book === chapterFilter.book &&
          (chapterFilter.chapter === undefined ||
            chapter.chapter === chapterFilter.chapter)
      );
      if (chapters.length === run.results.length) {
        // Same list
        result.push(run);
      } else if (chapters.length > 0) {
        // Some chapters removed; recalculate metadata
        let incorrect = 0;
        let correct = 0;
        let totalTime = 0;
        chapters.forEach((ch) => {
          incorrect += ch.incorrect.length;
          correct += ch.correct
            ? ch.correct.length
            : material[ch.book].chapters[ch.chapter].length - incorrect;
          totalTime += ch.time;
        });
        const total = incorrect + correct;
        result.push({
          ...run,
          results: chapters,
          totalVerses: total,
          percent: correct / total,
          averageTime: totalTime / total,
          hasAdditional: true,
        });
      }
    });
    return result;
  }, [runs, chapterFilter]);

  /**
   * Filters an array of runs by the specified dates
   */
  const getDateFilteredRuns = (runs: ReviewRun[]) => {
    const from = dayjs(dateFrom).startOf("day");
    const to = dayjs(dateTo).endOf("day");
    return runs.filter((run) => {
      const time = dayjs(run.runTime);
      if (time < from || time > to) return false;
      return true;
    });
  };

  // Runs, filtered by chapter and date
  const dateFilteredRuns = useMemo(
    () => getDateFilteredRuns(chapterFilteredRuns),
    [chapterFilteredRuns, dateFrom, dateTo]
  );
  const runModal = dateFilteredRuns[runIndexViewing];

  // Conglomerate stats added up
  const totalStats = useMemo(() => {
    let totalRuns = 0;
    let totalTime = 0;
    let totalReviewed = 0;
    dateFilteredRuns.forEach((run) => {
      totalRuns += 1;
      run.results.forEach((chapter) => {
        totalTime += chapter.time;
        totalReviewed += chapter.correct
          ? chapter.correct.length + chapter.incorrect.length
          : material[chapter.book].chapters[chapter.chapter].length;
      });
    });

    return {
      totalRuns,
      totalTime,
      totalReviewed,
    };
  }, [dateFilteredRuns]);

  // Stats grouped by chapter
  const chapterStats = useMemo(() => {
    const filteredRuns = getDateFilteredRuns(runs).reverse();
    const chapters: Record<
      string,
      {
        b: number;
        c: number;
        incorrect: number;
        correct: number;
        time: number;
        total: number;
        percent: number;
        flag: string;
        verses: {
          c: number;
          i: number;
          t: string;
        }[];
        totalVerses: number;
      }
    > = {};

    // Add up and group by chapter, with separate objects for each verse
    filteredRuns.forEach((run) => {
      run.results.forEach((chapter) => {
        const key = `${chapter.book}-${chapter.chapter}`;
        if (!chapters[key]) {
          chapters[key] = {
            b: chapter.book,
            c: chapter.chapter,
            incorrect: 0,
            correct: 0,
            time: 0,
            total: 0,
            percent: 0,
            flag: "",
            verses: [],
            totalVerses: 0,
          };
          for (
            let i = 0;
            i < material[chapter.book].chapters[chapter.chapter].length;
            i++
          ) {
            chapters[key].verses.push({ c: 0, i: 0, t: "" });
          }
        }

        chapters[key].incorrect += chapter.incorrect.length;
        chapter.incorrect.forEach((inc) => {
          chapters[key].verses[inc].i += 1;
          chapters[key].verses[inc].t = run.runTime;
        });
        if (chapter.correct) {
          chapters[key].correct += chapter.correct.length;
          chapters[key].total +=
            chapter.correct.length + chapter.incorrect.length;
          chapter.correct.forEach((cor) => {
            chapters[key].verses[cor].c += 1;
            chapters[key].verses[cor].t = run.runTime;
          });
        } else {
          const chapterLength =
            material[chapter.book].chapters[chapter.chapter].length;
          chapters[key].correct += chapterLength - chapter.incorrect.length;
          chapters[key].total += chapterLength;

          const incorrectSet = new Set(chapter.incorrect);
          for (
            let i = 0;
            i < material[chapter.book].chapters[chapter.chapter].length;
            i++
          ) {
            if (!incorrectSet.has(i)) {
              chapters[key].verses[i].c += 1;
              chapters[key].verses[i].t = run.runTime;
            }
          }
        }
        chapters[key].time += chapter.time;
      });
    });

    // Calculate result percent for each verse
    const list = Object.values(chapters)
      .map((chapter) => {
        let totalPercent = 0;
        let totalVerses = 0;
        chapter.verses.forEach((v) => {
          const sum = v.c + v.i;
          if (sum > 0) {
            totalPercent += v.c / sum;
            totalVerses += 1;
          }
        });
        chapter.percent = totalPercent / totalVerses;
        chapter.totalVerses = totalVerses;
        return chapter;
      })
      .sort((c1, c2) => c1.percent - c2.percent);

    // Calculate red or green for the top and bottom 20% of chapters
    if (list.length > 2) {
      const fifth = Math.max(Math.floor(list.length * 0.5), 1);
      for (let i = 0; i < fifth; i++) {
        list[i].flag = "R";
        list[list.length - 1 - i].flag = "G";
      }
    }
    return {
      list,
      map: chapters,
    };
  }, [runs, dateFrom, dateTo]);
  const chapterViewDetails =
    chapterStats.map[`${chapterViewing.book}-${chapterViewing.chapter}`];

  const overTime = useMemo(() => {
    // Calculate verses studied and overall percent. Only the previous month is included.
    const runOrder = [...chapterFilteredRuns].reverse();

    // Get date set and list
    const allDates = new Set<string>();
    const dateList: string[] = [];
    runOrder.forEach((run) => {
      const dateStr = dayjs(run.runTime).format("YYYY-MM-DD");
      if (!allDates.has(dateStr)) {
        allDates.add(dateStr);
        dateList.push(dateStr);
      }
    });

    // Single verse for a single day, one object for rolling, one for individual
    interface VerseDay {
      date: string;
      book: number;
      chapter: number;
      verse: number;
      cor: number;
      inc: number;
    }
    const indivVersesByDay: Record<string, VerseDay> = {};
    const rollingVersesByDay: Record<string, VerseDay> = {};

    const indivTimeByDay: Record<string, { time: number; total: number }> = {};
    const rollingTimeByDay: Record<string, { time: number; total: number }> =
      {};

    /**
     * Adds a verse to both objects
     */
    const addVerse = (
      key: string,
      date: string,
      chapter: ReviewChapter,
      verse: number,
      correct: boolean,
      includeIndiv: boolean
    ) => {
      if (!rollingVersesByDay[key]) {
        rollingVersesByDay[key] = {
          date,
          book: chapter.book,
          chapter: chapter.chapter,
          verse,
          cor: 0,
          inc: 0,
        };
      }
      if (includeIndiv) {
        if (!indivVersesByDay[key]) {
          indivVersesByDay[key] = {
            date,
            book: chapter.book,
            chapter: chapter.chapter,
            verse,
            cor: 0,
            inc: 0,
          };
        }
      }
      if (correct) {
        rollingVersesByDay[key].cor += 1;
        if (includeIndiv) indivVersesByDay[key].cor += 1;
      } else {
        rollingVersesByDay[key].inc += 1;
        if (includeIndiv) indivVersesByDay[key].inc += 1;
      }
    };

    // Add up every verse
    runOrder.forEach((run) => {
      for (let i = 0; i < 30; i++) {
        const date = dayjs(run.runTime).add(i, "days").format("YYYY-MM-DD");
        // For the rolling day
        if (!allDates.has(date)) continue;
        run.results.forEach((chapter) => {
          chapter.incorrect.forEach((verse) => {
            const key = `${date}-${chapter.book}-${chapter.chapter}-${verse}`;
            addVerse(key, date, chapter, verse, false, i === 0);
          });
          if (chapter.correct) {
            // Loop through correct verses
            chapter.correct.forEach((verse) => {
              const key = `${date}-${chapter.book}-${chapter.chapter}-${verse}`;
              addVerse(key, date, chapter, verse, true, i === 0);
            });
          } else {
            const incorrectSet = new Set(chapter.incorrect);
            // All other verses correct
            material[chapter.book].chapters[chapter.chapter].forEach(
              (_, verse) => {
                if (!incorrectSet.has(verse)) {
                  const key = `${date}-${chapter.book}-${chapter.chapter}-${verse}`;
                  addVerse(key, date, chapter, verse, true, i === 0);
                }
              }
            );
          }
        });

        if (i === 0) {
          if (!indivTimeByDay[date]) {
            indivTimeByDay[date] = {
              time: 0,
              total: 0,
            };
          }
          indivTimeByDay[date].time += run.averageTime * run.totalVerses;
          indivTimeByDay[date].total += run.totalVerses;
        }
        if (!rollingTimeByDay[date]) {
          rollingTimeByDay[date] = {
            time: 0,
            total: 0,
          };
        }
        rollingTimeByDay[date].time += run.averageTime * run.totalVerses;
        rollingTimeByDay[date].total += run.totalVerses;
      }
    });

    // Group verses from a day together
    const allDayVerses = Object.values(rollingVersesByDay);
    const allDayIndivVerses = Object.values(indivVersesByDay);
    const dayGrouped: Record<string, VerseDay[]> = {};
    allDayVerses.forEach((verse) => {
      if (!dayGrouped[verse.date]) dayGrouped[verse.date] = [];
      dayGrouped[verse.date].push(verse);
    });
    const indivDayGrouped: Record<string, VerseDay[]> = {};
    allDayIndivVerses.forEach((verse) => {
      if (!indivDayGrouped[verse.date]) indivDayGrouped[verse.date] = [];
      indivDayGrouped[verse.date].push(verse);
    });

    interface DayGrouped {
      date: string;
      dateDisplay: string;
      percent: number;
      indivPercent: number;
      total: number;
      indivTotal: number;

      time: number;
      indivTime: number;
    }
    const result: DayGrouped[] = [];
    dateList.forEach((date) => {
      const newObj: DayGrouped = {
        date,
        dateDisplay: date.substring(5), // For the x-axis label
        percent: 0,
        indivPercent: 0,
        total: dayGrouped[date].length,
        indivTotal: indivDayGrouped[date]?.length || 0,

        time: rollingTimeByDay[date].time / rollingTimeByDay[date].total,
        indivTime: indivTimeByDay[date].time / indivTimeByDay[date].total,
      };
      // Calculate percents
      dayGrouped[date].forEach((verse) => {
        newObj.percent += verse.cor / (verse.cor + verse.inc);
      });
      indivDayGrouped[date]?.forEach((verse) => {
        newObj.indivPercent += verse.cor / (verse.cor + verse.inc);
      });
      newObj.percent = (newObj.percent / newObj.total) * 100;
      newObj.indivPercent =
        newObj.indivTotal > 0
          ? (newObj.indivPercent / newObj.indivTotal) * 100
          : 0;
      result.push(newObj);
    });
    return result;
  }, [runs, chapterFilter]);

  // Filter options for chapter filter
  const bookSelections = useMemo(() => {
    const bookIndexes = new Set<number>();
    runs.forEach((run) => {
      run.results.forEach((result) => {
        bookIndexes.add(result.book);
      });
    });
    const sorted = Array.from(bookIndexes).sort();
    const result: EnumDescription<string> = {};
    sorted.forEach((index) => {
      result[String(index)] = material[index].bookName;
    });
    return result;
  }, [runs]);
  const chapterSelections = useMemo(() => {
    if (chapterFilter.book === undefined) return [];
    const result: EnumDescription<string> = {};
    material[chapterFilter.book].chapters.forEach((_, i) => {
      result[String(i)] = String(i + 1);
    });
    return result;
  }, [chapterFilter]);

  // Partial list of worst verses from the specified criteria
  const worstVerses = useMemo(() => {
    interface VerseStruct {
      book: number;
      chapter: number;
      verse: number;
      correct: number;
      incorrect: number;
      percent: number;
      lastReviewed: string;
      lastCorrect: boolean;
    }
    // Calculate percent for each verse
    const allVerses: Record<string, VerseStruct> = {};
    const newList = [...dateFilteredRuns].reverse();
    newList.forEach((run) => {
      run.results.forEach((chapter) => {
        // Determine correct and incorrect for each verse asked
        const chapterLength =
          material[chapter.book].chapters[chapter.chapter].length;
        const inc = new Set(chapter.incorrect);
        let cor = new Set();
        if (chapter.correct) {
          cor = new Set(chapter.correct);
        } else {
          for (let i = 0; i < chapterLength; i++) {
            if (!inc.has(i)) cor.add(i);
          }
        }
        for (let i = 0; i < chapterLength; i++) {
          if (inc.has(i) || cor.has(i)) {
            const key = `${chapter.book}-${chapter.chapter}-${i}`;
            if (!allVerses[key]) {
              allVerses[key] = {
                book: chapter.book,
                chapter: chapter.chapter,
                verse: i,
                correct: 0,
                incorrect: 0,
                percent: 0,
                lastReviewed: "",
                lastCorrect: false,
              };
            }
            allVerses[key].lastReviewed = run.runTime;
            allVerses[key].lastCorrect = !inc.has(i);
            if (inc.has(i)) {
              allVerses[key].incorrect += 1;
            } else {
              allVerses[key].correct += 1;
            }
          }
        }
      });
    });

    // Sort by the worst percentages and return the lowest 10%
    const list = Object.values(allVerses)
      .map((verse) => ({
        ...verse,
        percent: verse.correct / (verse.correct + verse.incorrect),
      }))
      .sort((v1, v2) => {
        const percent = v1.percent - v2.percent;
        if (percent) return percent;
        return v2.incorrect - v1.incorrect;
      });
    const result: VerseStruct[] = [];
    for (let i = 0; i < Math.max(list.length / 10, 3); i++) {
      if (!list[i] || list[i].incorrect === 0) break;
      result.push(list[i]);
    }
    return result;
  }, [dateFilteredRuns]);

  const getMainContent = () => {
    if (!user) {
      return (
        <>
          <p>Log in or create an account to track your Review Stats.</p>
          <Link to="/login" className="clickable">
            Log In Now
          </Link>
        </>
      );
    }
    if (!readStarted || (loading && runs.length === 0))
      return <CircularProgress size={70} />;

    if (runs.length === 0) {
      return <p>You don't have any review statistics yet.</p>;
    }
    const tableStyle = { maxHeight: "min(100vh - 100px, 430px)" };
    return (
      <>
        <Row>
          <div style={{ marginTop: 6 }}>
            <label htmlFor="date-from">Filter Date From</label>
          </div>
          <div style={{ marginLeft: 8, marginRight: 8 }}>
            <SimpleDateInput
              caption="Filter Date From"
              value={dateFrom}
              onChange={setDateFrom}
              id="date-from"
            />
          </div>
          <div style={{ marginTop: 6 }}>
            <label htmlFor="date-to">to</label>
          </div>
          <div style={{ marginLeft: 8 }}>
            <SimpleDateInput
              caption="Filter Date To"
              value={dateTo}
              onChange={setDateTo}
              id="date-to"
            />
          </div>
        </Row>
        <Row style={{ marginTop: 10 }}>
          <div style={{ marginTop: 4 }}>
            <span>Filter by Book/Chapter:</span>
          </div>
          <div style={{ marginLeft: 4, marginRight: 4 }}>
            <SimpleSelection
              caption="Book"
              values={bookSelections}
              value={String(chapterFilter.book || "")}
              onChange={(value) =>
                setChapterFilter({
                  book: value ? Number.parseInt(value) : undefined,
                })
              }
              noneText="All"
              dropdownStyle={{ width: 220 }}
            />
          </div>
          {chapterFilter.book !== undefined && (
            <SimpleSelection
              caption="Chapter"
              values={chapterSelections}
              value={
                chapterFilter.chapter === undefined
                  ? ""
                  : String(chapterFilter.chapter)
              }
              onChange={(value) =>
                setChapterFilter({
                  ...chapterFilter,
                  chapter: value ? Number.parseInt(value) : undefined,
                })
              }
              noneText="All"
              dropdownStyle={{ width: 100 }}
            />
          )}
        </Row>
        <Grid container spacing={3} style={{ marginTop: 20 }}>
          <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
            <Row>
              <h2 style={{ margin: 0 }}>All Verse Cards Runs</h2>
              &nbsp;{"-"}
              <button className="link" onClick={() => setTotalModalOpen(true)}>
                Total Stats
              </button>
            </Row>
            <DataTable
              style={tableStyle}
              getKey={(obj) => obj.runId}
              data={dateFilteredRuns}
              columns={[
                {
                  title: "Date",
                  render: (obj, index) => (
                    <button
                      className="link"
                      style={{ fontSize: 14 }}
                      onClick={() => {
                        setRunIndexViewing(index);
                        setRunModalOpen(true);
                        setDeletePhase(0);
                      }}
                    >
                      {getDateRangeDisplay(obj.runTime)}
                    </button>
                  ),
                  property: "time",
                  sortFunc: (obj) => obj.runTime,
                  defaultSortDirection: "desc",
                },
                {
                  title: "Material",
                  maxWidth: 250,
                  render: (obj) => {
                    // Only one chapter
                    let result = "";
                    if (obj.results.length === 1) {
                      const chapter = obj.results[0];
                      const fullLength =
                        material[chapter.book].chapters[chapter.chapter].length;
                      const verseCount = chapter.correct
                        ? chapter.correct.length + chapter.incorrect.length
                        : fullLength;
                      result = `${material[chapter.book].bookName} ${
                        chapter.chapter + 1
                      }${
                        verseCount !== fullLength
                          ? ` (${verseCount}/${fullLength})`
                          : ""
                      }`;
                    } else {
                      // Multiple chapters
                      const chaptersByBook: Record<string, number[]> = {}; // book, chapter list
                      obj.results.forEach((chapter) => {
                        const key = String(chapter.book);
                        if (!chaptersByBook[key]) chaptersByBook[key] = [];
                        chaptersByBook[key].push(chapter.chapter);
                      });
                      const allBooks = Object.keys(chaptersByBook);
                      if (allBooks.length <= 3) {
                        allBooks.forEach((bookStr) => {
                          const book = Number.parseInt(bookStr);
                          const chapters = chaptersByBook[bookStr];
                          result += `${
                            result.length === 0 ? "" : ", "
                          }${getShortenedBook(material[book].bookName)}${
                            chapters.length < material[book].chapters.length
                              ? ` (${chapters.length} ch)`
                              : ""
                          }`;
                        });
                      }
                    }
                    if (!result) {
                      // Fallback, just have the number of chapters
                      result = `${obj.results.length} Chapters`;
                    }

                    // Additional material, filtered out
                    if (obj.hasAdditional) result += " (+)";

                    return result;
                  },
                  property: "results",
                },
                {
                  title: "Verses",
                  render: (obj) => obj.totalVerses,
                  property: "totalVerses",
                  sortFunc: true,
                  defaultSortDirection: "desc",
                },
                {
                  title: "Percent",
                  render: (obj) =>
                    (obj.percent * 100).toFixed(1) +
                    (obj.percent >= 0.99 && obj.totalVerses > 10 ? " 🔥" : ""),
                  property: "percent",
                  sortFunc: (obj) => obj.percent,
                },
                {
                  title: "Avg. Time",
                  render: (obj) => obj.averageTime.toFixed(1),
                  property: "averageTime",
                  sortFunc: (obj) => obj.averageTime,
                },
              ]}
              fallbackContent={
                <p>No review runs for the specified criteria.</p>
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
            <Row>
              <h2 style={{ margin: 0 }}>Charts</h2>
              &nbsp;{"-"}
              <button onClick={() => setChartHelpOpen(true)} className="link">
                What do the lines mean?
              </button>
            </Row>
            <BQATabs
              tabs={[
                {
                  title: "Accuracy Over Time",
                  key: "PERCENT",
                },
                {
                  title: "Average Time Taken",
                  key: "TIME",
                },
                {
                  title: "Total Verses Studied",
                  key: "TOTAL",
                },
              ]}
              value={chartViewing}
              onChange={setChartViewing}
            />
            {chartViewing === "PERCENT" && (
              <Line
                key="Accuracy"
                options={{
                  maintainAspectRatio: true,
                  responsive: true,
                  animation: { duration: 0 }, // general animation time
                  // @ts-ignore
                  hover: { display: false, animationDuration: 0 }, // No animation
                  responsiveAnimationDuration: 0,
                  plugins: {
                    legend: {
                      display: true,
                      position: "top",
                      labels: getChartLabelOptions(lightMode),
                    },
                  },
                  tooltips: {
                    enabled: false,
                  },
                  scales: {
                    x: {
                      ticks: {
                        // @ts-ignore
                        callback: (x: number) => {
                          return overTime[x].dateDisplay;
                        },
                      },
                      grid: {
                        display: true,
                        drawOnChartArea: true,
                        color: "#666666",
                        // @ts-ignore
                        innerWidth: 0.5,
                      },
                    },
                    xAxes: {
                      display: false,
                    },
                    y: {
                      display: true,
                      position: "left",
                      grid: {
                        color: "#666666",
                      },
                      ticks: {
                        // @ts-ignore
                        callback: (y: number) => {
                          return `${y.toFixed(0)}%`;
                        },
                      },
                    },
                  },
                }}
                data={{
                  labels: overTime.map((score) => score.date),
                  datasets: [
                    {
                      data: overTime.map((time) => time.percent),
                      label: "Accuracy - Rolling 30 Days",
                      borderColor: "red",
                      borderWidth: 4,
                    },
                    {
                      data: overTime.map((time) => time.indivPercent),
                      label: "Accuracy Each Day",
                      borderColor: "blue",
                      borderWidth: 4,
                    },
                  ],
                }}
                style={{ maxHeight: 400 }}
              />
            )}
            {chartViewing === "TIME" && (
              <Line
                key="Average Time"
                options={{
                  maintainAspectRatio: true,
                  responsive: true,
                  animation: { duration: 0 }, // general animation time
                  // @ts-ignore
                  hover: { display: false, animationDuration: 0 }, // No animation
                  responsiveAnimationDuration: 0,
                  plugins: {
                    legend: {
                      display: true,
                      position: "top",
                      labels: getChartLabelOptions(lightMode),
                    },
                  },
                  tooltips: {
                    enabled: false,
                  },
                  scales: {
                    x: {
                      ticks: {
                        // @ts-ignore
                        callback: (x: number) => {
                          return overTime[x].dateDisplay;
                        },
                      },
                      grid: {
                        display: true,
                        drawOnChartArea: true,
                        color: "#666666",
                        // @ts-ignore
                        innerWidth: 0.5,
                      },
                    },
                    xAxes: {
                      display: false,
                    },
                    y: {
                      display: true,
                      position: "left",
                      grid: {
                        color: "#666666",
                      },
                      ticks: {
                        // @ts-ignore
                        callback: (y: number) => {
                          return y.toFixed(1);
                        },
                      },
                    },
                  },
                }}
                data={{
                  labels: overTime.map((score) => score.date),
                  datasets: [
                    {
                      data: overTime.map((time) => time.time),
                      label: "Average Time - Rolling 30 Days",
                      borderColor: "red",
                      borderWidth: 4,
                    },
                    {
                      data: overTime.map((time) => time.indivTime),
                      label: "Average Time Each Day",
                      borderColor: "blue",
                      borderWidth: 4,
                    },
                  ],
                }}
                style={{ maxHeight: 400 }}
              />
            )}
            {chartViewing === "TOTAL" && (
              <Line
                key="Total"
                options={{
                  maintainAspectRatio: true,
                  responsive: true,
                  animation: { duration: 0 }, // general animation time
                  // @ts-ignore
                  hover: { display: false, animationDuration: 0 }, // No animation
                  responsiveAnimationDuration: 0,
                  plugins: {
                    legend: {
                      display: true,
                      position: "top",
                      labels: getChartLabelOptions(lightMode),
                    },
                  },
                  tooltips: {
                    enabled: false,
                  },
                  scales: {
                    x: {
                      ticks: {
                        // @ts-ignore
                        callback: (x: number) => {
                          return overTime[x].date;
                        },
                      },
                      grid: {
                        display: true,
                        drawOnChartArea: true,
                        color: "#666666",
                        // @ts-ignore
                        innerWidth: 0.5,
                      },
                    },
                    xAxes: {
                      display: false,
                    },
                    y: {
                      grid: {
                        color: "#666666",
                      },
                    },
                  },
                }}
                data={{
                  labels: overTime.map((date) => date.date),
                  datasets: [
                    {
                      data: overTime.map((date) => date.total),
                      label: "Verses Reviewed - Last 30 Days",
                      borderColor: "red",
                      borderWidth: 4,
                    },
                    {
                      data: overTime.map((date) => date.indivTotal),
                      label: "Total Verses Each Day",
                      borderColor: "blue",
                      borderWidth: 4,
                    },
                  ],
                }}
                style={{ maxHeight: 400 }}
              />
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
            <Row>
              <h2 style={{ margin: 0 }}>Stats by Chapter</h2>&nbsp;{"-"}
              <button className="link" onClick={() => setPercentHelpOpen(true)}>
                Percentage Calculation
              </button>
            </Row>
            <DataTable
              data={chapterStats.list}
              style={tableStyle}
              getKey={(obj) => `${obj.b}-${obj.c}`}
              defaultSort={{
                property: "chapter",
                direction: "asc",
              }}
              onClickCell={(obj) => {
                if (
                  chapterFilter.book === obj.b &&
                  chapterFilter.chapter === obj.c
                ) {
                  setChapterFilter({});
                } else {
                  setChapterFilter({
                    book: obj.b,
                    chapter: obj.c,
                  });
                }
              }}
              rowDisplay={(obj) => {
                return {
                  selected:
                    chapterFilter.book === obj.b &&
                    chapterFilter.chapter === obj.c,
                };
              }}
              columns={[
                {
                  title: "Chapter",
                  property: "chapter",
                  render: (obj) => (
                    <button
                      className="link"
                      style={{ fontSize: 14 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setChapterModalOpen(true);
                        setChapterViewing({
                          book: obj.b,
                          chapter: obj.c,
                        });
                      }}
                    >
                      {`${material[obj.b].bookName} ${obj.c + 1}${
                        obj.totalVerses !==
                        material[obj.b].chapters[obj.c].length
                          ? ` (${obj.totalVerses}/${
                              material[obj.b].chapters[obj.c].length
                            })`
                          : ""
                      }`}
                    </button>
                  ),
                  sortFunc: (c) => c.b * 10000 + c.c * 100,
                  maxWidth: 245,
                },
                {
                  title: "Verses Asked",
                  property: "total",
                  render: (obj) => obj.total,
                  sortFunc: (obj) => obj.total,
                },
                {
                  title: "Incorrect",
                  property: "incorrect",
                  render: (obj) => obj.incorrect,
                  sortFunc: true,
                  defaultSortDirection: "desc",
                },
                {
                  title: "Percent",
                  property: "percent",
                  render: (obj) => (
                    <span
                      style={{
                        border: `1px solid ${
                          obj.flag === "R"
                            ? lightMode
                              ? "darkred"
                              : "red"
                            : obj.flag === "G"
                              ? lightMode
                                ? "darkgreen"
                                : "lightgreen"
                              : "transparent"
                        }`,
                        fontWeight: "bold",
                        borderRadius: 4,
                        padding: 3,
                      }}
                    >
                      {(obj.percent * 100).toFixed(1)}
                    </span>
                  ),
                  sortFunc: (obj) => obj.percent,
                },
                {
                  title: "Avg. Time",
                  property: "time",
                  render: (obj) =>
                    (obj.time / (obj.correct + obj.incorrect)).toFixed(1),
                  sortFunc: (obj) => obj.time,
                },
              ]}
              fallbackContent={<p>No chapter stats for this date range.</p>}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
            <Row>
              <h2 style={{ margin: 0 }}>Worst Verses</h2>
              {worstVerses.length > 0 && (
                <>
                  &nbsp;{"-"}
                  <button
                    className="link"
                    onClick={() => {
                      const newSelection: MaterialSelection = [];
                      material.forEach((book) => {
                        newSelection.push(
                          Array(book.chapters.length).fill(false)
                        );
                      });

                      worstVerses.forEach((verse) => {
                        const chapterSelection: boolean[] =
                          typeof newSelection[verse.book][verse.chapter] ===
                          "boolean"
                            ? (Array(
                                material[verse.book].chapters[verse.chapter]
                                  .length
                              ).fill(false) as boolean[])
                            : (newSelection[verse.book][
                                verse.chapter
                              ] as boolean[]);
                        chapterSelection[verse.verse] = true;
                        newSelection[verse.book][verse.chapter] =
                          chapterSelection;
                      });

                      navigate("/versecards", {
                        state: {
                          verseCards: {
                            material: newSelection,
                          },
                        },
                      });
                    }}
                  >
                    Review These Verses
                  </button>
                </>
              )}
            </Row>
            <DataTable
              data={worstVerses}
              style={tableStyle}
              getKey={(obj) => `${obj.book}-${obj.chapter}-${obj.verse}`}
              columns={[
                {
                  title: "Verse",
                  property: "verse",
                  render: (obj) => (
                    <button
                      className="link"
                      onClick={() => {
                        setVerseModalOpen(true);
                        setVerseModalSelection({
                          book: obj.book,
                          chapter: obj.chapter,
                          verse: obj.verse,
                        });
                      }}
                      style={{ fontSize: 14 }}
                    >
                      {`${material[obj.book].bookName} ${obj.chapter + 1}:${
                        obj.verse + 1
                      }`}
                    </button>
                  ),
                  sortFunc: (v) => v.book * 10000 + v.chapter * 100 + v.verse,
                  maxWidth: 250,
                },
                {
                  title: "Correct",
                  property: "correct",
                  render: (obj) => obj.correct,
                  sortFunc: true,
                },
                {
                  title: "Incorrect",
                  property: "incorrect",
                  render: (obj) => obj.incorrect,
                  sortFunc: true,
                },
                {
                  title: "Percent",
                  property: "percent",
                  render: (obj) =>
                    (
                      (obj.correct / (obj.correct + obj.incorrect)) *
                      100
                    ).toFixed(1),
                  sortFunc: (obj) =>
                    obj.correct / (obj.correct + obj.incorrect),
                },
                {
                  title: "Last Reviewed",
                  property: "lastReviewed",
                  render: (obj) =>
                    `${obj.lastCorrect ? "✓" : "×"} ${getDateRangeDisplay(
                      obj.lastReviewed
                    )}`,
                  sortFunc: (obj) => obj.lastReviewed,
                },
              ]}
              fallbackContent={<p>No worst verses!</p>}
            />
          </Grid>
        </Grid>
        <Modal
          title={`Run Details - ${getTimeStampDisplay(runModal?.runTime)}`}
          open={runModalOpen}
          onClose={() => setRunModalOpen(false)}
        >
          {runModal && (
            <>
              {deletePhase === 0 && (
                <>
                  <p>{`Overall Percent: ${(runModal.percent * 100).toFixed(
                    1
                  )}%`}</p>
                  <p>{`Average Time: ${runModal.averageTime.toFixed(
                    1
                  )} Seconds`}</p>
                  {runModal.results.map((chapter) => (
                    <div
                      key={`${chapter.book}-${chapter.chapter}`}
                      style={{ fontSize: 14 }}
                    >
                      <h3>{`${material[chapter.book].bookName} ${
                        chapter.chapter + 1
                      }`}</h3>
                      {chapter.incorrect.length > 0 && (
                        <p>
                          <b>{`Incorrect (${chapter.incorrect.length}):`}</b>
                          &nbsp;
                          <span>
                            {chapter.incorrect.map(
                              (v, i) => `${i > 0 ? ", " : ""}${v + 1}`
                            )}
                          </span>
                        </p>
                      )}
                      <p>
                        <b>Correct:</b>
                        &nbsp;
                        <span>
                          {chapter.correct
                            ? chapter.correct.map(
                                (v, i) => `${i > 0 ? ", " : ""}${v + 1}`
                              )
                            : `The ${
                                chapter.incorrect.length === 0
                                  ? "entire"
                                  : "rest of the"
                              } chapter`}
                        </span>
                      </p>
                    </div>
                  ))}
                  {runModal.hasAdditional && (
                    <p>(Plus other material that is filtered out)</p>
                  )}
                  <button
                    className="secondary-btn"
                    onClick={() => setDeletePhase(1)}
                    style={{ marginTop: 10 }}
                  >
                    Remove this Run
                  </button>
                </>
              )}
              {deletePhase === 1 && (
                <>
                  <p>
                    Only delete this run if it is not legitimate, or if you were
                    not the one who did the verse cards.
                  </p>
                  <Row style={{ marginTop: 20 }}>
                    <button
                      className="clickable"
                      onClick={() => setDeletePhase(2)}
                    >
                      Continue
                    </button>
                    <button
                      className="secondary-btn"
                      onClick={() => setDeletePhase(0)}
                      style={{ marginLeft: 10 }}
                    >
                      Cancel
                    </button>
                  </Row>
                </>
              )}
              {deletePhase === 2 && (
                <>
                  <p>Are you sure you want to delete this verse cards run?</p>
                  <Row style={{ marginTop: 20 }}>
                    <button
                      className="clickable"
                      onClick={() => {
                        handler.runRequest(
                          deleteReviewRun(runModal),
                          "Deleting...",
                          "Run removed.",
                          "DELETE"
                        );
                      }}
                    >
                      Confirm Delete
                    </button>
                    <button
                      className="secondary-btn"
                      onClick={() => setDeletePhase(0)}
                      style={{ marginLeft: 10 }}
                    >
                      Cancel
                    </button>
                  </Row>
                </>
              )}
            </>
          )}
        </Modal>
        <Modal
          title="Total Stats"
          open={totalModalOpen}
          onClose={() => setTotalModalOpen(false)}
        >
          <p>{`Total Verse Cards Runs: ${totalStats.totalRuns}`}</p>
          <p>{`Total Verses Reviewed: ${totalStats.totalReviewed}`}</p>
          <p>{`Time Spent: ${getTimeDisplay(totalStats.totalTime)}`}</p>
          {overTime.length > 0 && (
            <p>
              {`Last 30 Days: ${overTime[overTime.length - 1].percent.toFixed(
                1
              )}% across ${overTime[overTime.length - 1].total} Verses`}
            </p>
          )}
        </Modal>
        <Modal
          title={`${material[verseModalSelection.book].bookName} ${
            verseModalSelection.chapter + 1
          }:${verseModalSelection.verse + 1}`}
          open={verseModalOpen}
          onClose={() => setVerseModalOpen(false)}
        >
          <p>
            {
              material[verseModalSelection.book].chapters[
                verseModalSelection.chapter
              ][verseModalSelection.verse].verse
            }
          </p>
        </Modal>
        <Modal
          title={`Stats for ${material[chapterViewing.book].bookName} ${
            chapterViewing.chapter + 1
          }`}
          open={chapterModalOpen}
          onClose={() => setChapterModalOpen(false)}
        >
          {chapterViewDetails && (
            <>
              {chapterViewDetails.totalVerses ===
              material[chapterViewing.book].chapters[chapterViewDetails.c]
                .length ? (
                <button
                  className="link"
                  onClick={() => {
                    const newSelection: MaterialSelection = [];
                    material.forEach((book) => {
                      newSelection.push(
                        Array(book.chapters.length).fill(false)
                      );
                    });
                    newSelection[chapterViewing.book][chapterViewing.chapter] =
                      true;
                    navigate("/versecards", {
                      state: {
                        verseCards: {
                          material: newSelection,
                        },
                      },
                    });
                  }}
                >
                  Review this Chapter
                </button>
              ) : (
                <button
                  className="link"
                  onClick={() => {
                    const newSelection: MaterialSelection = [];
                    material.forEach((book) => {
                      newSelection.push(
                        Array(book.chapters.length).fill(false)
                      );
                    });
                    const thisChapter = Array(
                      material[chapterViewing.book].chapters[
                        chapterViewDetails.c
                      ].length
                    ).fill(true);

                    chapterViewDetails.verses.forEach((verse, index) => {
                      thisChapter[index] = verse.c === 0 && verse.i === 0;
                    });
                    newSelection[chapterViewing.book][chapterViewing.chapter] =
                      thisChapter;
                    navigate("/memorize", {
                      state: {
                        memorize: {
                          material: newSelection,
                        },
                      },
                    });
                  }}
                >
                  Memorize Remaining Verses
                </button>
              )}
              <DataTable
                data={chapterViewDetails.verses}
                columns={[
                  {
                    title: "Verse",
                    render: (_, index) => index + 1,
                    property: "index",
                    sortFunc: true,
                  },
                  {
                    title: "Correct",
                    render: (obj) => obj.c,
                    property: "c",
                    sortFunc: true,
                  },
                  {
                    title: "Incorrect",
                    render: (obj) => obj.i,
                    property: "i",
                    sortFunc: true,
                  },
                  {
                    title: "Percent",
                    render: (obj) =>
                      obj.c > 0 || obj.i > 0
                        ? ((obj.c / (obj.c + obj.i)) * 100).toFixed(1)
                        : "-",
                    property: "percent",
                    sortFunc: (obj) =>
                      obj.c > 0 || obj.i > 0 ? obj.c / (obj.c + obj.i) : 0,
                  },
                  {
                    title: "Last Reviewed",
                    render: (obj) => (obj.t ? getDateRangeDisplay(obj.t) : "-"),
                    property: "t",
                    sortFunc: (obj) => obj.t,
                  },
                ]}
                style={{ maxHeight: 500, marginTop: 10 }}
              />
            </>
          )}
        </Modal>
        <Modal
          open={chartHelpOpen}
          title="What do the lines on the charts mean?"
          onClose={() => setChartHelpOpen(false)}
        >
          <p>
            The{" "}
            <span style={{ color: "blue", fontWeight: "bold" }}>Blue Line</span>{" "}
            represents the review that you did on a particular day: how many
            verses you reviewed, and what was your percentage on those verses.
          </p>
          <p>
            The{" "}
            <span style={{ color: "red", fontWeight: "bold" }}>Red Line</span>{" "}
            represents the rolling 30 days, showing your trend over the last
            month. It is a combination of the last 30 days of review, so it will
            change slower than the blue line. If you miss a verse, it will
            continue affecting the rolling average for the next 30 days!
          </p>
          <p>
            In some cases, the rolling 30 days could increase despite a lower
            percentage for a single day. This could happen if you reviewed your
            worst verses, and even if you missed some of them, you increased
            your percentage on those verses, so your overall percentage
            increased.
          </p>
          <p>
            For the total verses chart, the last 30 day line could go down if
            there are some verses that you last reviewed over 30 days ago. If
            that happens, maybe you should review those verses again.
          </p>
          <a href="https://en.wikipedia.org/wiki/Moving_average">
            More Information on Rolling Averages
          </a>
        </Modal>
        <Modal
          title="How are Percentages Calculated?"
          open={percentHelpOpen}
          onClose={() => setPercentHelpOpen(false)}
        >
          <p>
            In <b>Stats by Chapter</b> and <b>Charts</b>, each verse that you
            have reviewed is weighted evenly. This means that each verse will
            impact the overall percentage the same as each other verse,
            regardless of how many times you have reviewed them. In other words,
            the percentage is the average of all the percentages of the
            individual verses. As a result, the calculated percentage may be
            slightly different than what the Verses Asked and Incorrect count
            might suggest.
          </p>
          <p>
            For example, suppose you have five verses that you know perfectly
            and have reviewed them ten times each, and you have another five
            verses that you don't know at all but have only reviewed them once
            each. Your resulting percentage will be 50%, because your overall
            knowledge is 50% across the ten verses, even though you have spent
            more time on the verses you know well.
          </p>
          <p>
            If you want to improve your percentages, the fastest way to do so is
            to review your worst verses and chapters, since their individual
            percentages stand to gain the most improvement.
          </p>
        </Modal>
      </>
    );
  };

  return (
    <div className="page">
      <Row style={{ marginBottom: 10 }}>
        <Link className="secondary-btn" to="/">
          Go Back
        </Link>
        <h1 style={{ marginLeft: 20 }}>Review Stats</h1>
      </Row>
      {getMainContent()}
      {handler.snackbar}
    </div>
  );
}
